import {
  SpacingProps,
  ColorProps,
  BorderProps,
  LayoutProps,
  BackgroundColorProps,
  OpacityProps,
  PositionProps,
  ShadowProps,
} from "@shopify/restyle";

import { Theme } from "../../../theme/restyle/theme";
import { ButtonVariant, ButtonSize } from "./Button";

// Define the restyle props that our button will support
export type RestyleProps = SpacingProps<Theme> &
  ColorProps<Theme> &
  BorderProps<Theme> &
  LayoutProps<Theme> &
  BackgroundColorProps<Theme> &
  OpacityProps<Theme> &
  PositionProps<Theme> &
  ShadowProps<Theme>;

/**
 * Get the base styles for the button based on variant and size
 */
export const getBaseStyles = (
  variant: ButtonVariant,
  size: ButtonSize
): RestyleProps => {
  const baseStyles: RestyleProps = {
    borderRadius: "2",
    alignItems: "center",
    justifyContent: "center",
    padding: 5,
  };

  // Add size-specific styles
  switch (size) {
    case "large":
      baseStyles.padding = 5;
      baseStyles.gap = 4;
      baseStyles.height = 56;
      break;
    case "regular":
      baseStyles.gap = 3;
      baseStyles.padding = 4;
      baseStyles.height = 48;
      break;
    case "small":
      baseStyles.gap = 2;
      baseStyles.paddingHorizontal = 3;
      baseStyles.paddingVertical = 4;
      baseStyles.height = 40;
      break;
    case "tiny":
      baseStyles.gap = 2;
      baseStyles.paddingHorizontal = 2;
      baseStyles.paddingVertical = 3;
      baseStyles.height = 32;
      break;
  }

  // Add variant-specific styles
  switch (variant) {
    case "main":
      baseStyles.backgroundColor = "button-main-surface-neutral";
      break;
    case "secondary":
      baseStyles.backgroundColor = "button-alt-surface-neutral";
      break;
    case "white":
      baseStyles.backgroundColor = "button-white-surface-neutral";
      break;
    case "danger":
      baseStyles.backgroundColor = "button-danger-surface-neutral";
      break;
    case "outline":
      baseStyles.backgroundColor = "transparent";
      break;
    case "ghost":
      baseStyles.backgroundColor = "transparent";
      break;
  }

  return baseStyles;
};

/**
 * Get the text styles for the button based on variant and size
 */
export const getTextStyles = (
  variant: ButtonVariant,
  size: ButtonSize,
  customColor?: keyof Theme["colors"]
) => {
  const textStyles: any = {};

  switch (variant) {
    case "main":
      textStyles.color = "button-main-text";
      break;
    case "secondary":
      textStyles.color = "button-alt-text";
      break;
    case "white":
      textStyles.color = "button-white-text";
      break;
    case "danger":
      textStyles.color = "button-danger-text";
      break;
    case "outline":
      textStyles.color = customColor || "button-ghost-text";
      break;
    case "ghost":
      textStyles.color = customColor || "button-ghost-text";
      break;
  }

  // Add size-specific text styles
  if (size === "tiny") {
    textStyles.variant = "mainTiny";
    textStyles.fontWeight = "semibold";
    textStyles.lineHeight = 16;
    textStyles.minHeight = 16;
  } else {
    textStyles.lineHeight = 18;
    textStyles.minHeight = 18;
    textStyles.variant = "mainBaseSemiBold";
  }

  return textStyles;
};

/**
 * Get the hover color for a button variant
 */
export const getHoverColor = (
  variant: ButtonVariant
): keyof Theme["colors"] => {
  switch (variant) {
    case "main":
      return "button-main-surface-hover";
    case "secondary":
      return "button-alt-surface-hover";
    case "white":
      return "button-white-surface-hover";
    case "danger":
      return "button-danger-surface-hover";
    case "outline":
      return "input-outline-hover";
    case "ghost":
      return "transparent";
    default:
      return "button-alt-surface-hover";
  }
};

/**
 * Get the pressed color for a button variant
 */
export const getPressedColor = (
  variant: ButtonVariant
): keyof Theme["colors"] => {
  switch (variant) {
    case "main":
      return "button-main-surface-pressed";
    case "secondary":
      return "button-alt-surface-pressed";
    case "white":
      return "button-white-surface-pressed";
    case "danger":
      return "button-danger-surface-pressed";
    case "outline":
      return "input-outline-pressed";
    case "ghost":
      return "transparent";
    default:
      return "button-alt-surface-pressed";
  }
};

/**
 * Get hover styles for a button variant
 */
export const getHoverStyles = (
  variant: ButtonVariant,
  isDisabled: boolean
): Partial<RestyleProps> => {
  if (isDisabled) {
    return {};
  }
  if (variant === "outline") {
    return {
      borderColor: getHoverColor(variant),
      backgroundColor: "transparent",
    };
  }
  if (variant === "ghost") {
    return {
      opacity: 0.7,
    };
  }
  return {
    backgroundColor: getHoverColor(variant),
  };
};

/**
 * Get pressed styles for a button variant
 */
export const getPressedStyles = (
  variant: ButtonVariant,
  isDisabled: boolean
): Partial<RestyleProps> => {
  if (isDisabled) {
    return {};
  }
  if (variant === "outline") {
    return {
      borderColor: getPressedColor(variant),
      backgroundColor: "transparent",
    };
  }
  if (variant === "ghost") {
    return {
      opacity: 0.5,
    };
  }
  return {
    backgroundColor: getPressedColor(variant),
  };
};

/**
 * Apply custom color styles for ghost and outline variants
 */
export const applyCustomColorStyles = (
  baseStyles: RestyleProps,
  variant: ButtonVariant,
  isDisabled: boolean,
  customColor?: keyof Theme["colors"]
): RestyleProps => {
  if (!isDisabled && variant === "outline") {
    baseStyles.backgroundColor = "transparent";
    baseStyles.borderWidth = 1.5;
    baseStyles.borderColor = customColor || "outline-default";
  }

  return baseStyles;
};
